import { useEffect } from "react";

import {
  faChartSimple,
  faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import {
  buildExplorePin,
  type ChartExploreContext,
} from "src/analytics/copilot/chartExploreContext";
import { DashboardChartCard } from "src/analytics/dashboard/DashboardChartCard";
import {
  useDashboardMetricsParams,
  usePinMetrics,
} from "src/analytics/dashboard/DashboardMetricsContext";
import { useJustPinned } from "src/analytics/dashboard/JustPinnedContext";
import { PinChartModal } from "src/analytics/dashboard/PinChartModal";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { isPerformanceDashboard } from "src/api/analytics-dashboard/fixedDashboards";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { extractTitle, SandboxedChart } from "src/chartSandbox/SandboxedChart";
import { type Pin, type Realm } from "src/clients/analytics-dashboard";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { downloadRowsAsCsv } from "src/utils/csvExport";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

const PinBody: React.FC<{
  rows: MetricsQueryRow[] | undefined;
  isLoading: boolean;
  error: Error | null;
  chartDefinition: Record<string, unknown>;
  labelDictionaries: LabelDictionaries;
}> = ({ rows, isLoading, error, chartDefinition, labelDictionaries }) => {
  if (isLoading) {
    return <Skeleton height="h-full" width="w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        description="Try widening the date range or switching between Live and Sandbox. There may not be any data yet for this query."
        headline="No data found"
        icon={faChartSimple}
        size="compact"
      />
    );
  }
  return (
    <SandboxedChart
      chartDefinition={chartDefinition}
      labelDictionaries={labelDictionaries}
      rows={rows}
    />
  );
};

export const DashboardPinChart: React.FC<{
  pin: Pin;
  realm: Realm;
  realmId: string;
  labelDictionaries: LabelDictionaries;
  dashboardId?: string;
  isEditing?: boolean;
  onExploreWithCopilot?: (structuredInput: ChartExploreContext) => void;
}> = ({
  pin,
  realm,
  realmId,
  labelDictionaries,
  dashboardId,
  isEditing = false,
  onExploreWithCopilot,
}) => {
  const { column, row, width, height } = pin.layout;
  const { unpinChart, updatePinTitle } = useDashboard(
    realm,
    realmId,
    dashboardId,
  );
  const { rows, error, isLoading } = usePinMetrics(pin.id);
  const { environment, timeWindow } = useDashboardMetricsParams();
  const { isJustPinned, dismiss } = useJustPinned();
  const editModal = useModal();
  const unpinModal = useModal();
  const { ref: cardRef, scrollIntoView } = useScrollIntoView<HTMLDivElement>({
    visibilityMargin: 0,
  });

  const chartDef = pin.definition.chart_definition as Record<string, unknown>;
  const displayTitle = pin.title || extractTitle(chartDef);
  const showDateFilterCallout = !isEditing && isJustPinned(pin.id);
  const isReadOnly = isPerformanceDashboard(realm, dashboardId);

  useEffect(() => {
    if (showDateFilterCallout) {
      scrollIntoView();
    }
  }, [showDateFilterCallout, scrollIntoView]);

  const canExplore = !!onExploreWithCopilot;
  const exploreDisabledReason = isLoading
    ? "Chart is still loading"
    : error
      ? "Chart failed to load"
      : !rows || rows.length === 0
        ? "Data loading..."
        : undefined;
  const handleExplore = canExplore
    ? () =>
        onExploreWithCopilot({
          pins: [buildExplorePin(pin, rows, environment, timeWindow)],
        })
    : undefined;

  // In edit mode react-grid-layout owns positioning and sizing; outside it the
  // chart places itself in the static CSS grid via column/row spans.
  return (
    <div
      ref={cardRef}
      className={isEditing ? "h-full" : "min-h-0"}
      data-loc="dashboard-pin-chart"
      style={
        isEditing
          ? undefined
          : {
              gridColumn: `${column} / span ${width}`,
              gridRow: `${row} / span ${height}`,
            }
      }
    >
      <DashboardChartCard
        callout={
          showDateFilterCallout ? (
            <Callout
              action={{ text: "Close", onClick: () => dismiss(pin.id) }}
              dataLoc="dashboard-pin-date-filter-callout"
              variant="warning"
            >
              Time range adjusted to match the dashboard's date filter.
            </Callout>
          ) : undefined
        }
        chartDefinition={chartDef}
        isEditing={isEditing}
        exploreDisabled={exploreDisabledReason}
        onEdit={isReadOnly ? undefined : editModal.openModal}
        onExplore={handleExplore}
        onExport={
          !error && rows && rows.length > 0
            ? () => downloadRowsAsCsv(rows, displayTitle)
            : undefined
        }
        onUnpin={isReadOnly ? undefined : unpinModal.openModal}
        title={pin.title}
      >
        <PinBody
          chartDefinition={chartDef}
          error={error}
          isLoading={isLoading}
          labelDictionaries={labelDictionaries}
          rows={rows}
        />
      </DashboardChartCard>
      <PinChartModal
        afterLeave={editModal.afterLeave}
        defaultTitle={displayTitle}
        mode="edit"
        open={editModal.isOpen}
        onClose={editModal.closeModal}
        onConfirm={(title) => {
          updatePinTitle(pin.id, title);
          editModal.closeModal();
        }}
      >
        <PinBody
          chartDefinition={chartDef}
          error={error}
          isLoading={isLoading}
          labelDictionaries={labelDictionaries}
          rows={rows}
        />
      </PinChartModal>
      <ConfirmationModal
        afterLeave={unpinModal.afterLeave}
        confirmationButtonText="Unpin chart"
        dataLoc="dashboard-pin-unpin-confirmation"
        open={unpinModal.isOpen}
        title={`Unpin the "${displayTitle}" chart?`}
        variant="danger"
        onClose={unpinModal.closeModal}
        onConfirm={() => {
          unpinChart(pin.id);
          unpinModal.closeModal();
        }}
      >
        <div className="font-inter-normal-12px text-gray-500">
          Are you sure you want to unpin the "{displayTitle}" chart from this
          dashboard? This action cannot be undone.
        </div>
      </ConfirmationModal>
    </div>
  );
};
